<section class="fap-page">
  <!-- 0.45.3: FATIP rebrand across user-facing surfaces. Internal code
       identifiers (CSS prefixes, event types, API paths, SpiceDB
       object ids) stay on the FAP acronym — this is cosmetic
       platform-facing naming. The full expansion lives in the
       subtitle so first-time users understand the acronym. -->
  <h1>{{ 'i18n.fap.landing.welcome' | translate }}</h1>
  <p class="fap-brand-subtitle">
    {{ 'i18n.fap.landing.brand_subtitle' | translate }}
  </p>

  @if (notice() === 'module_disabled') {
    <div class="fap-alert fap-alert--warning" role="status" aria-live="polite">
      {{ 'i18n.fap.landing.module_disabled_notice' | translate }}
    </div>
  }

  <div class="fap-toolbar" role="search">
    <!-- U13 task 4 (S20): the hardcoded "Suchen" literal became the existing
         search_placeholder key (the org-tree filter's pattern), on the input,
         which takes a label. -->
    <input
      type="search"
      class="fap-search"
      [ngModel]="query()"
      (ngModelChange)="query.set($event)"
      [placeholder]="'i18n.fap.landing.search_placeholder' | translate"
      [attr.aria-label]="'i18n.fap.landing.search_placeholder' | translate" />
    <small class="fap-hint">{{ 'i18n.fap.landing.search_hint' | translate }}</small>

    <!-- Cut U6 (task 6): the tenant filter that stood here is gone. Its
         `<select>` wrote a `mandant` signal that no computed on this page
         read, so picking a tenant moved nothing; and the catalog it sat over
         has no tenant axis at all, because the backend resolves the modules
         under the caller's own tenant. Every control left in this toolbar
         changes what the reader sees: the search box, the favourites chip and
         the sort chip change what the list below shows, and the view toggle
         changes how it is rendered. That is what
         `landing-toolbar-controls.component.spec.ts` reads out of the DOM. -->
    <span class="fap-spacer"></span>

    <button type="button"
            class="fap-chip"
            [class.fap-chip--on]="onlyFavourites()"
            (click)="onlyFavourites.set(!onlyFavourites())">
      <fap-icon name="star" [size]="14"></fap-icon> {{ 'i18n.fap.landing.only_favourites' | translate }}
    </button>

    <button type="button" class="fap-chip" (click)="toggleSort()">
      @if (sortAsc()) { ↓ {{ 'i18n.fap.landing.sort_az' | translate }} }
      @else           { ↑ {{ 'i18n.fap.landing.sort_za' | translate }} }
    </button>

    <!-- U13 task 3 (S19): the API list's view-toggle markup
         (apis-list.component.ts), not a second implementation: translated
         group label, a title and a .sr-only label per button, the glyph
         hidden from assistive technology. The keys are the API list's,
         which already exist in all four locales. -->
    <div class="fap-view-toggle" role="group" [attr.aria-label]="'i18n.fap.module.apis.view_toggle' | translate">
      <button type="button"
              [class.active]="view() === 'grid'"
              (click)="view.set('grid')"
              [attr.title]="'i18n.fap.module.apis.view_cards' | translate">
        <span aria-hidden="true">▦</span>
        <span class="sr-only">{{ 'i18n.fap.module.apis.view_cards' | translate }}</span>
      </button>
      <button type="button"
              [class.active]="view() === 'list'"
              (click)="view.set('list')"
              [attr.title]="'i18n.fap.module.apis.view_list' | translate">
        <span aria-hidden="true"><fap-icon name="menu" [size]="14"></fap-icon></span>
        <span class="sr-only">{{ 'i18n.fap.module.apis.view_list' | translate }}</span>
      </button>
    </div>
  </div>

  <!-- Cut U3 (0.72.59): four mutually exclusive states in front of both views.
       Until this cut the catalog started from `signal([])` with no loading
       flag, and the only message was `no_match` in the grid view, so the
       first paint and a failed load both told the reader their search had
       matched nothing, and the list view rendered four table headers over no
       rows and said nothing at all. The chain sits OUTSIDE the grid/list
       branch on purpose: both views need all four, and an error banner
       stacked over an empty sentence is the shape the cut removes. -->
  @if (loading()) {
    <p class="fap-catalog-loading" role="status" aria-live="polite">
      {{ 'i18n.fap.landing.loading' | translate }}
    </p>
  } @else if (loadFailed()) {
    <fap-empty-state
      mode="error"
      messageKey="i18n.fap.landing.load_failed"
      retryKey="i18n.fap.landing.retry"
      (retry)="load()" />
  } @else if (catalogItems().length === 0) {
    <fap-empty-state messageKey="i18n.fap.landing.empty" />
  } @else if (visible().length === 0) {
    @if (onlyFavourites()) {
      <!-- U54 (issue #597): the favourites filter produced the empty set;
           the state names the filter, not the search, and carries the way
           back out (the U25 pattern). -->
      <p class="fap-catalog-empty">
        {{ 'i18n.fap.landing.no_favourites' | translate }}
        <button type="button" class="fap-catalog-empty__clear" (click)="onlyFavourites.set(false)">
          {{ 'i18n.fap.landing.clear_filter' | translate }}
        </button>
      </p>
    } @else {
      <!-- U25 (L94): the no-match state carries the way back out, so a search
           that matched nothing does not dead-end the reader. -->
      <p class="fap-catalog-empty">
        {{ 'i18n.fap.landing.no_match' | translate }}
        <button type="button" class="fap-catalog-empty__clear" (click)="clearSearch()">
          {{ 'i18n.fap.landing.clear_search' | translate }}
        </button>
      </p>
    }
  } @else if (view() === 'grid') {
    <!-- 0.42.0: the backend already hides modules the caller can't reach.
         The `enabled=false` state now only means "wired backend, but
         the tile is still in preview" — the landing treats it as a
         disabled anchor (same as before), NOT as "no permission".

         0.57.46 ADR-027: Sektion-Trennlinien (Identitäten / Zugriff &
         Anwendungen / …) entfernt zugunsten eines flachen Tile-Grids.
         Vorteile: kein Scrollen durch synthetische Kategorien, schnelleres
         Auge-Pattern-Match, weniger visueller Lärm. Favoriten erscheinen
         per `flatModules()` immer ganz oben (Sortierung statt Sektion);
         der Favoriten-Stern macht sie sichtbar ohne Header-Text. -->
    <div class="fap-tile-grid" role="list">
      @for (m of flatModules(); track m.id) {
        <ng-container *ngTemplateOutlet="tile; context: { $implicit: m }"></ng-container>
      }
    </div>

    <!-- U13 task 6 (S22): `role="listitem"` on the anchor replaced the
         implicit link role and took the tile out of the links list. The item
         role moved to this real `<li>` (the grid keeps its `role="list"`),
         so the anchor is a link again and the tiles stay the items of the
         list. -->
    <ng-template #tile let-m>
      <li>
        <a
          class="fap-tile"
          [class.fap-tile--disabled]="!m.enabled"
          [attr.aria-disabled]="!m.enabled"
          [attr.aria-label]="title(m) + ' — ' + desc(m)"
          [routerLink]="m.enabled ? m.entry_route : null"
          (click)="!m.enabled && $event.preventDefault()">
          <div class="fap-tile__head">
            <div class="fap-tile__icon" aria-hidden="true">
              <fap-icon [name]="iconFor(m.icon, m.id)" [size]="32"></fap-icon>
            </div>
            <button type="button"
                    class="fap-tile__fav"
                    [class.fap-tile__fav--on]="isFav(m)"
                    (click)="toggleFav(m, $event)"
                    [attr.aria-label]="isFav(m) ? ('i18n.fap.landing.remove_favourite' | translate) : ('i18n.fap.landing.add_favourite' | translate)">
              <fap-icon name="star" [size]="14"></fap-icon>
            </button>
          </div>
          <h3 class="fap-tile__title">{{ title(m) }}</h3>
          <p class="fap-tile__desc">{{ desc(m) }}</p>
          <div class="fap-tile__meta">
            <span>FATIP</span>
            @if (!m.enabled) {
              <span>{{ 'i18n.fap.landing.coming_soon' | translate }}</span>
            } @else {
              <span>{{ 'i18n.fap.landing.open' | translate }} →</span>
            }
          </div>
        </a>
      </li>
    </ng-template>
  } @else {
    <div class="fap-table-scroll">
    <table class="fap-table" role="table">
      <thead>
        <tr>
          <th>{{ 'i18n.fap.landing.col_module' | translate }}</th>
          <th>{{ 'i18n.fap.landing.col_description' | translate }}</th>
          <th>{{ 'i18n.fap.landing.col_status' | translate }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        @for (m of visible(); track m.id) {
          <tr>
            <td>{{ title(m) }}</td>
            <td>{{ desc(m) }}</td>
            <td>
              @if (m.enabled) { {{ 'i18n.fap.landing.status_active' | translate }} }
              @else           { {{ 'i18n.fap.landing.coming_soon' | translate }} }
            </td>
            <td>
              @if (m.enabled) {
                <a [routerLink]="m.entry_route">{{ 'i18n.fap.landing.open' | translate }} →</a>
              }
            </td>
          </tr>
        }
      </tbody>
    </table>
    </div>
  }
</section>
